import {
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  sendPasswordResetEmail,
  getAuth
} from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import { logger } from '../utils/logger.ts';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  getDocsFromServer
} from 'firebase/firestore';
import { auth, db, firebaseConfig } from '../firebase.js';
import {
  User,
  UserRole,
  Permission,
  LoginCredentials,
  RegistrationData,
  ROLE_PERMISSIONS,
  ALLOWED_ROLES,
  isValidRole
} from '../types/auth.ts';
import { rateLimitService } from './rateLimitService.ts';

// Firebase instances are imported from firebase.js

class AuthService {
  // Collection reference for users
  private usersCollection = 'users';

  /**
   * 🔒 P2 보안 개선: Rate Limiting 적용된 로그인
   *
   * - 연속 5회 실패 시 5분 차단
   * - 로그인 성공 시 실패 카운터 초기화
   */
  async login(credentials: LoginCredentials): Promise<User> {
    const { email, password } = credentials;

    // 🔒 Rate Limiting 체크
    const rateLimitCheck = rateLimitService.checkRateLimit(email);
    if (rateLimitCheck.blocked) {
      logger.warn('[AuthService] Login blocked due to rate limiting', {
        context: { email: email.substring(0, 3) + '***' }
      });
      const error = new Error(rateLimitCheck.message || '로그인이 일시적으로 차단되었습니다.');
      (error as any).code = 'auth/too-many-requests';
      (error as any).rateLimited = true;
      (error as any).remainingTime = rateLimitCheck.remainingTime;
      throw error;
    }

    try {
      logger.debug('[AuthService] Login attempt', { context: { email } });

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      logger.debug('[AuthService] Firebase auth successful', { context: { uid: userCredential.user.uid } });

      // Get user data from Firestore
      const userData = await this.getUserData(userCredential.user.uid);

      logger.debug('[AuthService] User data retrieved', {
        context: {
          uid: userData.uid,
          email: userData.email,
          role: userData.role
        }
      });

      // Update last login
      await this.updateLastLogin(userCredential.user.uid);

      // 🔒 로그인 성공 시 Rate Limiting 카운터 초기화
      rateLimitService.recordSuccessfulLogin(email);

      logger.debug('[AuthService] Login successful');
      return userData;
    } catch (error: any) {
      logger.error('[AuthService] Login error', error instanceof Error ? error : new Error(error.message));

      // 🔒 로그인 실패 기록 및 Rate Limiting 적용
      const rateLimitResult = await rateLimitService.recordFailedAttempt(
        email,
        this.getErrorMessage(error.code)
      );

      // Provide more detailed error information
      let errorMessage = this.getErrorMessage(error.code);

      // Rate Limiting 메시지 추가
      if (rateLimitResult.message) {
        errorMessage = `${errorMessage} ${rateLimitResult.message}`;
      }

      const detailedError = new Error(errorMessage);
      (detailedError as any).code = error.code;
      (detailedError as any).originalError = error;
      (detailedError as any).remainingAttempts = rateLimitResult.remainingAttempts;
      (detailedError as any).rateLimited = rateLimitResult.blocked;

      throw detailedError;
    }
  }

  // Logout current user
  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw new Error('로그아웃 중 오류가 발생했습니다.');
    }
  }

  // Send password reset email
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      logger.debug('[AuthService] Sending password reset email', { context: { email } });
      await sendPasswordResetEmail(auth, email);
      logger.debug('[AuthService] Password reset email sent successfully');
    } catch (error: any) {
      logger.error('[AuthService] Password reset email error', error instanceof Error ? error : new Error(error.message));
      
      const errorMessage = this.getPasswordResetErrorMessage(error.code);
      const detailedError = new Error(errorMessage);
      (detailedError as any).code = error.code;
      (detailedError as any).originalError = error;
      
      throw detailedError;
    }
  }

  // Register new user
  async register(data: RegistrationData): Promise<User> {
    try {
      // Create Firebase auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        data.email, 
        data.password
      );
      
      // Update display name
      if (data.displayName) {
        await updateProfile(userCredential.user, {
          displayName: data.displayName
        });
      }
      
      // Create user document in Firestore
      const userData: User = {
        uid: userCredential.user.uid,
        email: data.email,
        displayName: data.displayName,
        phoneNumber: null, // 기본값 undefined
        role: data.role || 'viewer',
        permissions: ROLE_PERMISSIONS[data.role || 'viewer'] || [],
        createdAt: new Date(),
        lastLogin: new Date()
      };
      
      await setDoc(doc(db, this.usersCollection, userCredential.user.uid), userData);
      
      return userData;
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  // Get current user data
  async getCurrentUser(): Promise<User | null> {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;
    
    return await this.getUserData(firebaseUser.uid);
  }

  // 사용자의 실제 권한 계산
  private calculateUserPermissions(userData: any): Permission[] {
    const rolePermissions = ROLE_PERMISSIONS[userData.role] || [];
    const customPermissions = userData.customPermissions || [];
    const revokedPermissions = userData.revokedPermissions || [];
    
    // 역할 권한 + 추가 권한 - 제거된 권한
    const allPermissions = [...new Set([...rolePermissions, ...customPermissions])];
    return allPermissions.filter(permission => 
      !revokedPermissions.includes(permission)
    );
  }

  // Get user data from Firestore
  private async getUserData(uid: string): Promise<User> {
    try {
      logger.debug('[AuthService] Fetching user data', { context: { uid } });
      const userDoc = await getDoc(doc(db, this.usersCollection, uid));
      
      if (!userDoc.exists()) {
        logger.error('[AuthService] User document not found in Firestore', new Error(`User not found: ${uid}`));
        throw new Error('사용자 정보를 찾을 수 없습니다.');
      }
      
      const data = userDoc.data();
      logger.debug('[AuthService] User document data retrieved', { context: { data } });
      
      // Validate required fields
      if (!data.email || !data.role) {
        logger.warn('[AuthService] Missing required fields in user document', {
          context: {
            hasEmail: !!data.email,
            hasRole: !!data.role
          }
        });
      }
      
      const calculatedPermissions = this.calculateUserPermissions(data);
      
      const user = {
        uid,
        email: data.email,
        displayName: data.displayName,
        phoneNumber: data.phoneNumber,
        role: data.role,
        permissions: calculatedPermissions, // 계산된 권한 사용
        customPermissions: data.customPermissions,
        revokedPermissions: data.revokedPermissions,
        teamId: data.teamId,
        // 🔗 계정↔직원 연결. 여기서 빠뜨리면 저장은 되는데 화면에는 다시 '연결 안 함'으로
        //    보이고, 본인 급여 열람 판정도 영영 false가 된다.
        employeeId: data.employeeId,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate() || new Date()
      };
      
      logger.debug('[AuthService] User data processed successfully');
      return user;
    } catch (error: any) {
      logger.error('[AuthService] Error in getUserData', error instanceof Error ? error : new Error(error.message));
      throw error;
    }
  }

  // Update last login timestamp
  private async updateLastLogin(uid: string): Promise<void> {
    try {
      logger.debug('[AuthService] Updating last login', { context: { uid } });
      await updateDoc(doc(db, this.usersCollection, uid), {
        lastLogin: new Date()
      });
      logger.debug('[AuthService] Last login updated successfully');
    } catch (error: any) {
      logger.error('[AuthService] Error updating last login', error instanceof Error ? error : new Error(error.message));
      // Don't throw - this is not critical for login success
    }
  }

  // Update user profile
  async updateUserProfile(uid: string, updates: Partial<User>): Promise<User> {
    try {
      // 🔧 FIX C3: 프로필 수정 권한 검증 - 본인 또는 관리자만 허용
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('인증되지 않은 사용자입니다.');
      }
      if (currentUser.uid !== uid) {
        // 관리자 확인
        const currentUserDoc = await getDoc(doc(db, this.usersCollection, currentUser.uid));
        const currentUserData = currentUserDoc.data();
        if (!currentUserData || currentUserData.role !== 'admin') {
          throw new Error('다른 사용자의 프로필을 수정할 권한이 없습니다.');
        }
      }

      logger.debug('[AuthService] Updating user profile', { context: { uid, updates } });

      // Update Firestore document
      await updateDoc(doc(db, this.usersCollection, uid), {
        ...updates,
        lastUpdated: new Date()
      });
      
      // If displayName is being updated, also update Firebase Auth profile
      if (updates.displayName !== undefined && auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: updates.displayName
        });
      }
      
      // Return updated user data
      const updatedUser = await this.getUserData(uid);
      logger.debug('[AuthService] User profile updated successfully');
      return updatedUser;
    } catch (error: any) {
      logger.error('[AuthService] Error updating user profile', error instanceof Error ? error : undefined);
      throw new Error('프로필 업데이트 중 오류가 발생했습니다.');
    }
  }

  // Update user phone number
  async updateUserPhoneNumber(uid: string, phoneNumber: string): Promise<User> {
    try {
      logger.debug('[AuthService] Updating phone number', { context: { uid } });
      
      // Update Firestore document
      await updateDoc(doc(db, this.usersCollection, uid), {
        phoneNumber: phoneNumber || null,
        lastUpdated: new Date()
      });
      
      // Return updated user data
      const updatedUser = await this.getUserData(uid);
      logger.debug('[AuthService] Phone number updated successfully');
      return updatedUser;
    } catch (error: any) {
      logger.error('[AuthService] Error updating phone number', error instanceof Error ? error : undefined);
      throw new Error('전화번호 업데이트 중 오류가 발생했습니다.');
    }
  }

  // Update user role
  // 🔧 FIX: permissions 필드도 함께 업데이트 (Firestore rules에서 permissions 필드 참조)
  async updateUserRole(uid: string, newRole: UserRole): Promise<void> {
    const userDocRef = doc(db, this.usersCollection, uid);
    const userDoc = await getDoc(userDocRef);
    const userData = userDoc.exists() ? userDoc.data() : {};
    const customPermissions = userData.customPermissions || [];

    // 새 역할의 기본 권한 + 기존 커스텀 권한으로 permissions 계산
    const rolePermissions = ROLE_PERMISSIONS[newRole] || [];
    const combinedPermissions = [...new Set([...rolePermissions, ...customPermissions])];

    await updateDoc(userDocRef, {
      role: newRole,
      permissions: combinedPermissions,
      // 역할 변경 시 제거된 권한은 초기화
      revokedPermissions: []
    });
  }

  // Update user team
  async updateUserTeam(uid: string, teamId: string): Promise<void> {
    await updateDoc(doc(db, this.usersCollection, uid), {
      teamId: teamId || null
    });
  }

  /**
   * 계정 ↔ 직원 명단 수동 연결.
   *
   * 🔒 이름 자동 매칭은 절대 하지 않는다. 동명이인이 한 명이라도 있으면
   *    엉뚱한 사람의 급여가 "본인 급여"로 열린다. 연결은 항상 원장이 화면에서 지정한다.
   *
   * 한 직원은 한 계정에만 연결된다. 이미 다른 계정에 물린 직원을 지정하면 거부한다.
   * (같은 직원이 두 계정에 물리면 둘 다 그 사람의 급여를 본인 것으로 열람하게 된다.)
   */
  async updateUserEmployeeId(uid: string, employeeId: string): Promise<void> {
    const normalized = (employeeId || '').trim();

    if (normalized) {
      const duplicated = await getDocs(
        query(collection(db, this.usersCollection), where('employeeId', '==', normalized))
      );
      const conflict = duplicated.docs.find((d) => d.id !== uid);
      if (conflict) {
        throw new Error(
          `이 직원은 이미 다른 계정(${conflict.data().email || conflict.id})에 연결되어 있습니다. 먼저 그 계정의 연결을 해제하세요.`
        );
      }
    }

    await updateDoc(doc(db, this.usersCollection, uid), {
      employeeId: normalized || null
    });
  }

  // 개별 사용자 권한 업데이트
  // 🔧 FIX: permissions 필드도 함께 재계산하여 Firestore rules와 동기화
  async updateUserPermissions(
    uid: string,
    customPermissions?: Permission[],
    revokedPermissions?: Permission[]
  ): Promise<void> {
    const userDocRef = doc(db, this.usersCollection, uid);
    const userDoc = await getDoc(userDocRef);
    const userData = userDoc.exists() ? userDoc.data() : {};

    const updateData: any = {};

    if (customPermissions !== undefined) {
      updateData.customPermissions = customPermissions;
    }

    if (revokedPermissions !== undefined) {
      updateData.revokedPermissions = revokedPermissions;
    }

    // permissions 필드 재계산: rolePermissions + customPermissions - revokedPermissions
    const rolePermissions = ROLE_PERMISSIONS[userData.role as UserRole] || [];
    const finalCustom = customPermissions ?? userData.customPermissions ?? [];
    const finalRevoked = revokedPermissions ?? userData.revokedPermissions ?? [];
    const combinedPermissions = [...new Set([...rolePermissions, ...finalCustom])]
      .filter(p => !finalRevoked.includes(p));
    updateData.permissions = combinedPermissions;

    updateData.lastUpdated = new Date();

    await updateDoc(userDocRef, updateData);
  }

  // 사용자에게 권한 추가
  async addPermissionToUser(uid: string, permission: Permission): Promise<void> {
    const userData = await this.getUserData(uid);
    const customPermissions = userData.customPermissions || [];
    const revokedPermissions = userData.revokedPermissions || [];
    
    // 커스텀 권한에 추가
    if (!customPermissions.includes(permission)) {
      customPermissions.push(permission);
    }
    
    // 제거된 권한에서 삭제
    const newRevokedPermissions = revokedPermissions.filter(p => p !== permission);
    
    await this.updateUserPermissions(uid, customPermissions, newRevokedPermissions);
  }

  // 사용자에게서 권한 제거
  async removePermissionFromUser(uid: string, permission: Permission): Promise<void> {
    const userData = await this.getUserData(uid);
    const customPermissions = userData.customPermissions || [];
    const revokedPermissions = userData.revokedPermissions || [];
    
    // 제거된 권한에 추가
    if (!revokedPermissions.includes(permission)) {
      revokedPermissions.push(permission);
    }
    
    // 커스텀 권한에서 삭제
    const newCustomPermissions = customPermissions.filter(p => p !== permission);
    
    await this.updateUserPermissions(uid, newCustomPermissions, revokedPermissions);
  }

  // Check if user has specific permission
  hasPermission(user: User | null, permission: Permission): boolean {
    if (!user) return false;
    return user.permissions.includes(permission);
  }

  // Check if user has any of the specified permissions
  hasAnyPermission(user: User | null, permissions: Permission[]): boolean {
    if (!user) return false;
    return permissions.some(permission => user.permissions.includes(permission));
  }

  // Check if user has all of the specified permissions
  hasAllPermissions(user: User | null, permissions: Permission[]): boolean {
    if (!user) return false;
    return permissions.every(permission => user.permissions.includes(permission));
  }

  // Get all users (admin only)
  async getAllUsers(): Promise<User[]> {
    try {
      // First sync current user to ensure they're in Firestore
      await this.syncCurrentUserToFirestore();
      
      const usersSnapshot = await getDocsFromServer(collection(db, this.usersCollection));
      const allUsers: User[] = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        const calculatedPermissions = this.calculateUserPermissions(data);
        
        return {
          uid: doc.id,
          email: data.email,
          displayName: data.displayName,
          phoneNumber: data.phoneNumber,
          role: data.role,
          permissions: calculatedPermissions,
          customPermissions: data.customPermissions,
          revokedPermissions: data.revokedPermissions,
          teamId: data.teamId,
          // 🔗 계정↔직원 연결. 여기서 빠뜨리면 저장은 되는데 화면에는 다시 '연결 안 함'으로
          //    보이고, 본인 급여 열람 판정도 영영 false가 된다.
          employeeId: data.employeeId,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLogin: data.lastLogin?.toDate() || new Date()
        };
      });

      logger.debug('[AuthService] Loaded users from Firestore', { context: { count: allUsers.length } });
      return allUsers;
    } catch (error) {
      logger.error('[AuthService] Error loading users', error instanceof Error ? error : undefined);
      throw error;
    }
  }

  // Get users by team
  async getUsersByTeam(teamId: string): Promise<User[]> {
    const q = query(
      collection(db, this.usersCollection), 
      where('teamId', '==', teamId)
    );
    const usersSnapshot = await getDocs(q);
    return usersSnapshot.docs.map(doc => {
      const data = doc.data();
      const calculatedPermissions = this.calculateUserPermissions(data);
      
      return {
        uid: doc.id,
        email: data.email,
        displayName: data.displayName,
        phoneNumber: data.phoneNumber,
        role: data.role,
        permissions: calculatedPermissions,
        customPermissions: data.customPermissions,
        revokedPermissions: data.revokedPermissions,
        teamId: data.teamId,
        // 🔗 계정↔직원 연결. 여기서 빠뜨리면 저장은 되는데 화면에는 다시 '연결 안 함'으로
        //    보이고, 본인 급여 열람 판정도 영영 false가 된다.
        employeeId: data.employeeId,
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate() || new Date()
      };
    });
  }

  // Sync current Firebase Auth user to Firestore
  async syncCurrentUserToFirestore(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await this.ensureUserInFirestore(currentUser.uid, {
          email: currentUser.email!,
          displayName: currentUser.displayName,
          role: 'viewer', // 기본 역할
          createdAt: new Date(),
          lastLogin: new Date()
        });
      }
    } catch (error) {
      logger.error('[AuthService] Error syncing current user', error instanceof Error ? error : undefined);
    }
  }

  // 사용자가 Firestore에 있는지 확인하고 없으면 생성하는 메서드
  private async ensureUserInFirestore(uid: string, userData: {
    email: string;
    displayName?: string | null;
    role?: UserRole;
    createdAt?: Date;
    lastLogin?: Date;
  }): Promise<void> {
    try {
      const userDoc = await getDoc(doc(db, this.usersCollection, uid));
      
      if (!userDoc.exists()) {
        logger.debug('[AuthService] Creating missing user in Firestore', { context: { uid } });
        
        const userRole = userData.role || 'viewer';
        const newUserData = {
          email: userData.email,
          displayName: userData.displayName || null,
          phoneNumber: null, // 기본값 undefined
          role: userRole,
          permissions: ROLE_PERMISSIONS[userRole] || [], // 🔧 FIX: Firestore rules용 permissions 필드
          customPermissions: [],
          revokedPermissions: [],
          createdAt: userData.createdAt || new Date(),
          lastLogin: userData.lastLogin || new Date()
        };
        
        await setDoc(doc(db, this.usersCollection, uid), newUserData);
        logger.debug('[AuthService] User created in Firestore successfully');
      }
    } catch (error) {
      logger.error('[AuthService] Error ensuring user in Firestore', error instanceof Error ? error : undefined);
    }
  }

  /**
   * 🔒 P0 보안 개선: 관리자가 새로운 사용자 생성
   *
   * 세컨더리 Firebase App 인스턴스를 사용하여 사용자 생성
   * → 관리자 세션이 손실되지 않음 (기존 버그 수정)
   *
   * 기존 문제점:
   * - createUserWithEmailAndPassword()가 자동으로 새 사용자로 로그인
   * - 관리자 세션이 손실됨
   *
   * 해결 방법:
   * - 세컨더리 Firebase App 인스턴스 생성
   * - 세컨더리 Auth로 사용자 생성 (메인 Auth 영향 없음)
   * - 작업 완료 후 세컨더리 App 삭제
   */
  async createUserByAdmin(userData: {
    email: string;
    password: string;
    displayName?: string;
    phoneNumber?: string;
    role: UserRole;
  }): Promise<User> {
    // 세컨더리 앱 인스턴스 (스코프 밖에서 선언하여 finally에서 정리 가능)
    let secondaryApp: ReturnType<typeof initializeApp> | null = null;

    try {
      // 🔧 FIX C12: 역할 값 검증 - 허용되지 않은 역할로 사용자 생성 차단
      if (!isValidRole(userData.role)) {
        throw new Error(`Invalid role: ${userData.role}. Allowed roles: ${ALLOWED_ROLES.join(', ')}`);
      }

      logger.debug('[AuthService] Admin creating new user', { context: { email: userData.email } });

      // 현재 관리자 정보 저장 (Firestore 기록용)
      const currentAdminUid = auth.currentUser?.uid || 'admin';

      // 🔒 세컨더리 Firebase App 인스턴스 생성
      // 이렇게 하면 메인 auth 인스턴스의 로그인 상태가 영향받지 않음
      const secondaryAppName = `secondary-${Date.now()}`;
      secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);

      logger.debug('[AuthService] Secondary Firebase app created', {
        context: { appName: secondaryAppName }
      });

      // 세컨더리 Auth로 새 사용자 생성
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        userData.email,
        userData.password
      );

      const newUser = userCredential.user;
      logger.debug('[AuthService] New user created in secondary auth', {
        context: { uid: newUser.uid }
      });

      // 표시 이름 설정
      if (userData.displayName) {
        await updateProfile(newUser, {
          displayName: userData.displayName
        });
      }

      // 세컨더리 Auth에서 새 사용자 로그아웃
      await signOut(secondaryAuth);

      // Firestore에 사용자 데이터 저장
      const firestoreUserData = {
        email: userData.email,
        displayName: userData.displayName || null,
        phoneNumber: userData.phoneNumber || null,
        role: userData.role,
        permissions: ROLE_PERMISSIONS[userData.role as UserRole] || [],
        customPermissions: [],
        revokedPermissions: [],
        createdAt: new Date(),
        lastLogin: new Date(),
        createdBy: currentAdminUid
      };

      await setDoc(doc(db, this.usersCollection, newUser.uid), firestoreUserData);

      logger.debug('[AuthService] New user created successfully (admin session preserved)');

      return {
        uid: newUser.uid,
        email: userData.email,
        displayName: userData.displayName || null,
        phoneNumber: userData.phoneNumber || null,
        role: userData.role,
        permissions: [], // 계산된 권한으로 덮어쓰므로 빈 배열
        customPermissions: [],
        revokedPermissions: [],
        createdAt: new Date(),
        lastLogin: new Date()
      };
    } catch (error: any) {
      logger.error('[AuthService] Error creating user by admin', error instanceof Error ? error : undefined);
      throw new Error(this.getErrorMessage(error.code));
    } finally {
      // 🔒 세컨더리 앱 정리 (메모리 누수 방지)
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
          logger.debug('[AuthService] Secondary Firebase app deleted');
        } catch (cleanupError) {
          logger.warn('[AuthService] Failed to delete secondary app', {
            context: { error: cleanupError }
          });
        }
      }
    }
  }

  // 관리자가 사용자 삭제 (Firestore만 삭제, Firebase Auth는 클라이언트 SDK로 불가능)
  async deleteUserFromFirestore(uid: string): Promise<void> {
    try {
      logger.debug('[AuthService] Deleting user from Firestore', { context: { uid } });
      
      // 현재 로그인한 사용자는 삭제할 수 없음
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === uid) {
        throw new Error('현재 로그인한 사용자는 삭제할 수 없습니다.');
      }
      
      // Firestore에서 사용자 문서 삭제
      await deleteDoc(doc(db, this.usersCollection, uid));

      logger.debug('[AuthService] User deleted from Firestore successfully');
    } catch (error: any) {
      logger.error('[AuthService] Error deleting user', error instanceof Error ? error : undefined);
      throw new Error(`사용자 삭제 중 오류가 발생했습니다: ${error.message}`);
    }
  }

  // Subscribe to auth state changes
  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userData = await this.getUserData(firebaseUser.uid);
          callback(userData);
        } catch (error) {
          logger.error('[AuthService] Error fetching user data', error instanceof Error ? error : undefined);
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  }

  // Get error message based on Firebase error code
  private getErrorMessage(errorCode: string): string {
    logger.debug('[AuthService] Translating error code', { context: { errorCode } });
    
    switch (errorCode) {
      case 'auth/user-not-found':
        return '사용자를 찾을 수 없습니다.';
      case 'auth/wrong-password':
        return '잘못된 비밀번호입니다.';
      case 'auth/invalid-credential':
        return '잘못된 이메일 또는 비밀번호입니다.';
      case 'auth/email-already-in-use':
        return '이미 사용 중인 이메일입니다.';
      case 'auth/weak-password':
        return '비밀번호는 6자 이상이어야 합니다.';
      case 'auth/invalid-email':
        return '유효하지 않은 이메일 형식입니다.';
      case 'auth/user-disabled':
        return '이 계정은 비활성화되었습니다.';
      case 'auth/operation-not-allowed':
        return '이메일/비밀번호 로그인이 활성화되지 않았습니다.';
      case 'auth/too-many-requests':
        return '너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요.';
      case 'auth/network-request-failed':
        return '네트워크 연결 오류가 발생했습니다.';
      case 'auth/requires-recent-login':
        return '이 작업을 수행하려면 다시 로그인해야 합니다.';
      case 'auth/invalid-action-code':
        return '유효하지 않은 작업 코드입니다.';
      case 'auth/expired-action-code':
        return '만료된 작업 코드입니다.';
      default:
        return `인증 중 오류가 발생했습니다. (${errorCode})`;
    }
  }

  private getPasswordResetErrorMessage(errorCode: string): string {
    logger.debug('[AuthService] Translating password reset error code', { context: { errorCode } });
    
    switch (errorCode) {
      case 'auth/user-not-found':
        return '등록되지 않은 이메일입니다. 이메일 주소를 확인해주세요.';
      case 'auth/invalid-email':
        return '유효하지 않은 이메일 형식입니다.';
      case 'auth/too-many-requests':
        return '너무 많은 요청이 있었습니다. 잠시 후 다시 시도해주세요.';
      case 'auth/user-disabled':
        return '비활성화된 계정입니다. 관리자에게 문의하세요.';
      case 'auth/network-request-failed':
        return '네트워크 연결 오류가 발생했습니다.';
      default:
        return `비밀번호 재설정 이메일 전송 중 오류가 발생했습니다. (${errorCode})`;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export helper functions for easy access
export const hasPermission = (user: User | null, permission: Permission) => 
  authService.hasPermission(user, permission);

export const hasAnyPermission = (user: User | null, permissions: Permission[]) => 
  authService.hasAnyPermission(user, permissions);

export const hasAllPermissions = (user: User | null, permissions: Permission[]) =>
  authService.hasAllPermissions(user, permissions);

/**
 * 🔒 P1 보안 개선: 권한 계산 함수 (실시간 동기화용 export)
 * 역할 기본 권한 + 추가 권한 - 제거된 권한
 *
 * 기본 동작: 하드코딩된 ROLE_PERMISSIONS 사용
 * 동적 권한이 필요한 경우 calculateUserPermissionsAsync 사용
 */
export const calculateUserPermissions = (userData: any): Permission[] => {
  const rolePermissions = ROLE_PERMISSIONS[userData?.role] || [];
  const customPermissions = userData?.customPermissions || [];
  const revokedPermissions = userData?.revokedPermissions || [];

  // 역할 권한 + 추가 권한 - 제거된 권한
  const allPermissions = [...new Set([...rolePermissions, ...customPermissions])];
  return allPermissions.filter(permission =>
    !revokedPermissions.includes(permission)
  );
};

/**
 * 🔒 P2 보안 개선: 동적 권한 계산 함수 (비동기)
 *
 * Firestore의 roleDefinitions 컬렉션에서 역할 권한을 동적으로 로드
 * 런타임에 역할별 권한 변경 가능
 *
 * @example
 * ```typescript
 * import { calculateUserPermissionsAsync } from './authService';
 * import { roleDefinitionService } from './roleDefinitionService';
 *
 * // 동적 권한 계산
 * const permissions = await calculateUserPermissionsAsync(userData, roleDefinitionService);
 * ```
 */
export const calculateUserPermissionsAsync = async (
  userData: any,
  roleDefinitionService?: { getRolePermissions: (role: UserRole) => Promise<Permission[]> }
): Promise<Permission[]> => {
  let rolePermissions: Permission[];

  // 동적 역할 정의 서비스 사용 시
  if (roleDefinitionService && userData?.role) {
    try {
      rolePermissions = await roleDefinitionService.getRolePermissions(userData.role);
    } catch {
      // 실패 시 폴백
      rolePermissions = ROLE_PERMISSIONS[userData?.role] || [];
    }
  } else {
    rolePermissions = ROLE_PERMISSIONS[userData?.role] || [];
  }

  const customPermissions = userData?.customPermissions || [];
  const revokedPermissions = userData?.revokedPermissions || [];

  // 역할 권한 + 추가 권한 - 제거된 권한
  const allPermissions = [...new Set([...rolePermissions, ...customPermissions])];
  return allPermissions.filter(permission =>
    !revokedPermissions.includes(permission)
  );
};